import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  BalanceIndicator,
  JournalVoucherStyles,
  ReferenceCells,
  TYPE_OPTIONS,
  fetchOpenReferences,
  lineTotals,
  money,
  setLineReference,
  validateLines,
} from "./JournalVoucherShared";
import { subCategoryOptions } from "./SubCategory";

const Create = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const [chartOfAccount, setChartOfAccount] = useState({});
  const [costCenter, setCostCenter] = useState([]);
  const [budgetPlan, setBudgetPlan] = useState({});
  const [subCategory, setSubCategory] = useState({});
  const [journalNo, setJournalNo] = useState({});
  const [error, setError] = useState({});
  const [debitSum, setDebitSum] = useState(0);
  const [creditSum, setCreditSum] = useState(0);
  const [journalCode, setJournalCode] = useState(0);
  const { checkRequiredFields } = useRequiredFields();
  const [selectedOption, setSelectedOption] = useState(null);
  const [saving, setSaving] = useState(false);
  // Existing Reference options, keyed by account.
  const [openReferences, setOpenReferences] = useState({});

  const listState = [
    {
      chart_of_account_id: "",
      acc_cost_center_id: "",
      debit: "",
      credit: "",
      sub_category: "",
      budget_id: "",
      bill_no: "",
      bill_date: "",
      mature_day: "",
      remarks: "",
      reference: "",
      existing_reference: "",
    },
    {
      chart_of_account_id: "",
      acc_cost_center_id: "",
      debit: "",
      credit: "",
      sub_category: "",
      budget_id: "",
      bill_no: "",
      bill_date: "",
      mature_day: "",
      remarks: "",
      reference: "",
      existing_reference: "",
    },
  ];
  const [list, setList] = useState(listState);

  // Each row's ledgers arrive keyed by row index, so the options are derived
  // once per fetch rather than on every render of every row.
  const subCategoryOptionsByRow = useMemo(() => {
    const byRow = {};
    Object.keys(subCategory).forEach((key) => {
      byRow[key] = subCategoryOptions(subCategory[key]);
    });
    return byRow;
  }, [subCategory]);

  const selectHandleChange = (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  // The sequence restarts each month and runs per type, so the number depends
  // on both the journal type and the voucher date.
  const fetchJournalNo = (type, date) => {
    Http.get("/journal-no", {
      params: { type: type || "journal", date: date || inputs.date },
    })
      .then((res) => {
        setJournalNo(res.data);
      })
      .catch(function (e) {
        console.error("Could not read the next journal number:", e);
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Each journal type carries its own sequence, so the number shown belongs
    // to the type that is selected.
    if (name === "type") fetchJournalNo(value, inputs.date);
  };
  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };
  const handleDateChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // A different month is a different sequence.
    fetchJournalNo(inputs.type, value);

    inputs.date = value;
    Http.post("/budget-plan-search", inputs)
      .then((res) => {
        setBudgetPlan(res.data);
      })
      .catch(function (e) {
        setError(e.response.data.errors);
      });
  };

  // Set Experience State
  const handleInputChange = (e, index) => {
    const { name, value } = e.target; // Chanages Experience Input Name and Value
    const inputInfo = [...list]; // Defined Experience State All Information into List Variable
    let debit = 0;
    let credit = 0;

    inputInfo[index][name] = value;
    if (name == "debit") inputInfo[index]["credit"] = "";
    else if (name == "credit") inputInfo[index]["debit"] = "";

    list.forEach((item, itemIndex) => {
      // if(itemIndex <= index){
      debit += item.debit > 0 ? parseFloat(item.debit) : 0;
      credit += item.credit > 0 ? parseFloat(item.credit) : 0;
      // }
    });

    setDebitSum(debit);
    setCreditSum(credit);
    setList(inputInfo);
  };

  // Chart of Account
  /*const handleCOAChange = (e, index) => {
    const { name, value } = e.target; // Chanages Experience Input Name and Value
    const inputInfo = [...list]; // Defined Experience State All Information into List Variable
    inputInfo[index][name] = value;

    Http.get(
      "/subsidiary-ledger-search-by-chart-of-account/" + value + "/" + index
    ).then((res) => {
      setSubCategory((values) => ({ ...values, [index]: res.data }));
    });

    setList(inputInfo);
  };*/
  const selectHandleCOAChange = (value, index) => {
    setSelectedOption({ value: value, index: index });
    const inputInfo = [...list]; // Defined Experience State All Information into List Variable
    inputInfo[index]["chart_of_account_id"] = value;
    // The sub category list belongs to the account, so a pick made against the
    // previous account no longer applies. So do its references.
    inputInfo[index]["sub_category"] = "";
    inputInfo[index]["reference"] = "";
    inputInfo[index]["existing_reference"] = "";

    fetchOpenReferences(value)
      .then((options) =>
        setOpenReferences((values) => ({ ...values, [value]: options }))
      )
      .catch((e) => console.error("Could not load open references:", e));

    Http.get(
      "/subsidiary-ledger-search-by-chart-of-account/" + value + "/" + index
    ).then((res) => {
      console.log('API Response for subcategory:', res.data);
      setSubCategory((values) => ({ ...values, [index]: res.data }));
    }).catch((error) => {
      console.error('Error fetching subcategory data:', error);
      setSubCategory((values) => ({ ...values, [index]: {} }));
    });

    setList(inputInfo);
  };

  // Cost center is optional, so a cleared picker puts the line back to blank.
  const selectHandleCostCenterChange = (selectedOption, index) => {
    const inputInfo = [...list];
    inputInfo[index]["acc_cost_center_id"] = selectedOption
      ? selectedOption.value
      : "";
    setList(inputInfo);
  };

  const handleReferenceChange = (index, name, value) => {
    const inputInfo = [...list];
    inputInfo[index] = setLineReference(inputInfo[index], name, value);
    setList(inputInfo);
  };

  const selectHandleSubCategoryChange = (selectedOption, index) => {
    const inputInfo = [...list];
    inputInfo[index]["sub_category"] = selectedOption ? selectedOption.value : "";
    setList(inputInfo);
  };

  const getCurrentDateInput = () => {
    const dateObj = new Date();

    // get the month in this format of 04, the same for months
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();

    const shortDate = `${year}-${month}-${day}`;

    setInputs((values) => ({ ...values, ["date"]: shortDate }));
  };

  // Default Load
  const fetchData = () => {
    Http.get("/chart-of-account-code-2").then((res) => {
      setChartOfAccount(res.data);
    });
    Http.get("/cost-center-select2").then((res) => {
      setCostCenter(res.data);
    });
    // Http.get("/journal-no").then((res) => {
    //   setJournalNo(res.data);
    // });
    // Http.get("/journal-code").then((res) => {
    //   setJournalCode(res.data);
    // });

    Http.get("/journalCodeSelect2").then((res) => {
      setJournalCode(res.data);
    });

    setInputs({ type: "journal" });

    // Budget Setup
    const dateObj = new Date();
    // get the month in this format of 04, the same for months
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();

    const shortDate = `${year}-${month}-${day}`;

    // The form opens on a journal dated today, so it opens on that sequence.
    fetchJournalNo("journal", shortDate);

    inputs.date = shortDate;
    Http.post("/budget-plan-search", inputs)
      .then((res) => {
        setBudgetPlan(res.data);
      })
      .catch(function (e) {
        setError(e.response.data.errors);
      });
  };
  useEffect(() => {
    fetchData();
    getCurrentDateInput();
  }, []);
  // End Default Load

  // Add More List
  const addMoreList = () => {
    setList([
      ...list,
      {
        chart_of_account_id: "",
        acc_cost_center_id: "",
        debit: "",
        credit: "",
        sub_category: "",
        budget_id: "",
        bill_no: "",
        bill_date: "",
        mature_day: "",
        remarks: "",
        reference: "",
        existing_reference: "",
      },
    ]); // Add One More List
  };
  // Remove List
  const removeList = (index) => {
    const inputInfo = [...list];
    inputInfo.splice(index, 1);

    const totals = lineTotals(inputInfo);
    setDebitSum(totals.debit);
    setCreditSum(totals.credit);

    // Sub category options are keyed by row index, so the rows below the one
    // removed move up a key with it.
    setSubCategory((values) => {
      const next = {};
      Object.keys(values).forEach((key) => {
        const row = Number(key);
        if (row < index) next[row] = values[key];
        else if (row > index) next[row - 1] = values[key];
      });
      return next;
    });

    setList(inputInfo);
  };

  const post = (payload) => {
    setSaving(true);
    Http.post("/journal-voucher", payload)
      .then((res) => {
        navigate("/journal-voucher", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch((e) => {
        setSaving(false);
        setError(
          e.response?.data?.errors || {
            error: [
              e.response?.data?.message ||
                "Could not save the voucher. Please try again.",
            ],
          }
        );
      });
  };

  const formSubmit = () => {
    if (saving) return;
    checkRequiredFields();

    // Validated from the list itself: reading `error` here would see the
    // previous render's value, which made a valid save after any failed one
    // do nothing until it was clicked again.
    const result = validateLines(list);
    const errors = {};
    if (!String(inputs.remarks || "").trim()) {
      errors.remarks = ["Remarks is required."];
    }
    if (result.message) {
      errors.error = [result.message];
    }
    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }
    setError({});

    const payload = {
      ...inputs,
      serial_number: journalNo,
      items: result.items,
      total_amount: result.total,
    };

    const missingSubCategory = list.some(
      (line, index) =>
        line.chart_of_account_id > 0 &&
        !line.sub_category &&
        Object.keys(subCategory[index] || {}).length > 0
    );

    if (!missingSubCategory) {
      post(payload);
      return;
    }

    Swal.fire({
      title: "Submit without a sub category?",
      text: "Some lines use an account that has sub categories, but none is selected.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, submit",
    }).then((answer) => {
      if (answer.isConfirmed) post(payload);
    });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <JournalVoucherStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              New Journal Voucher{" "}
              {journalNo && typeof journalNo !== "object" && (
                <span className="jv-serial">{journalNo}</span>
              )}
            </h5>
            <p className="text-muted mb-0 tx-12">
              Post a balanced set of debit and credit lines
            </p>
          </div>
          <Link to="/journal-voucher" className="btn btn-outline-secondary">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>
        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          <div className="jv-form-panel">
            <div className="jv-form-head">
              <span>
                <i className="fa fa-file-text-o me-2"></i>Voucher Information
              </span>
            </div>
            <div className="jv-form-body">
            <div className="row">
              <div className="col-lg">
                <label>
                  Date <i className="fa fa-star required"></i>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  value={inputs.date || ""}
                  onChange={handleDateChange}
                  data-required
                />
                {displayError("date")}
              </div>
              <div className="col-lg">
                <label htmlFor="">Journal Code</label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  defaultValue={journalCode[0]}
                  name="journal_code"
                  options={journalCode}
                  onChange={selectHandleChange}
                />
                {/* <select
                  className="form-control"
                  name="journal_code"
                  onChange={handleChange}
                >
                  <option value="">Select Journal Code</option>
                  {Array.from(journalCode).map((item, index) => (
                    <option value={item.id} key={item.id}>
                      {item.code + " - " + item.name}
                    </option>
                  ))}
                </select> */}

                {displayError("journal_code")}
              </div>
              <div className="col-lg">
                <label htmlFor="">Journal Type</label>
                <select
                  className="form-control"
                  name="type"
                  value={inputs.type || "journal"}
                  onChange={handleChange}
                  data-required
                >
                  {TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {displayError("type")}
              </div>
              <div className="col-lg">
                <label>Journal No.</label>
                <input
                  type="text"
                  className="form-control"
                  name="journal_no"
                  value={journalNo || ""}
                  onChange={handleChange}
                  readOnly
                  data-required
                />

                {displayError("journal_no")}
              </div>
            </div>
            </div>
          </div>

          <div className="jv-form-panel">
            <div className="jv-form-head">
              <span>
                <i className="fa fa-list-ul me-2"></i>Journal Lines
              </span>
              <BalanceIndicator debit={debitSum} credit={creditSum} />
            </div>
            <div className="jv-form-body">
            <div className="row">
              <div className="col-lg table-responsive p-0 pb-2">
                <table
                  className="table table-sm align-items-center mb-0 jv-line-table"
                  style={{ minWidth: "2150px" }}
                >
                  <colgroup>
                    <col style={{ minWidth: "240px" }} />
                    <col style={{ minWidth: "220px" }} />
                    <col style={{ minWidth: "120px" }} />
                    <col style={{ minWidth: "120px" }} />
                    <col style={{ minWidth: "180px" }} />
                    <col style={{ minWidth: "200px" }} />
                    <col style={{ minWidth: "180px" }} />
                    <col style={{ minWidth: "220px" }} />
                    <col style={{ minWidth: "130px" }} />
                    <col style={{ minWidth: "150px" }} />
                    <col style={{ minWidth: "110px" }} />
                    <col style={{ minWidth: "220px" }} />
                    <col style={{ minWidth: "60px" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>
                        Account
                      </th>
                      <th>
                        Cost Center
                      </th>
                      <th className="jv-num">
                        Debit
                      </th>
                      <th className="jv-num">
                        Credit
                      </th>
                      <th>
                        Budget
                      </th>
                      <th>
                        Sub Category
                      </th>
                      <th>
                        Reference
                      </th>
                      <th>
                        Existing Reference
                      </th>
                      <th>
                        Bill No
                      </th>
                      <th>
                        Bill Date
                      </th>
                      <th>
                        Mature Day
                      </th>
                      <th>
                        Remarks
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(list).map((x, i) => (
                      <tr key={i}>
                        <td>
                          <div className="col-lg">
                            {/* <select
                            className="form-control"
                            name="chart_of_account_id"
                            onChange={(e) => handleCOAChange(e, i)}
                          >
                            <option value="">Select Chart of Account</option>
                            {Array.from(chartOfAccount).map((item, index) => (
                              <option value={item.id} key={item.id}>
                                {item.code + " - " + item.name}
                              </option>
                            ))}
                          </select> */}
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="chart_of_account_id"
                              options={chartOfAccount}
                              value={
                                Array.from(chartOfAccount).find(
                                  (option) =>
                                    String(option.value) ===
                                    String(x.chart_of_account_id)
                                ) || null
                              }
                              placeholder="Select Account"
                              onChange={(selectedOption) => {
                                // Extract the value and index from the selected option
                                const { value } = selectedOption;
                                selectHandleCOAChange(value, i);
                              }}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                              data-required
                            />

                            {displayError("chart_of_account_id")}
                          </div>
                        </td>
                        <td>
                          <div className="col-lg">
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              placeholder="Select Cost Center"
                              name="acc_cost_center_id"
                              options={costCenter}
                              value={
                                Array.from(costCenter).find(
                                  (option) =>
                                    String(option.value) ===
                                    String(x.acc_cost_center_id)
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                selectHandleCostCenterChange(selectedOption, i)
                              }
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                              maxMenuHeight={320}
                              isClearable
                              isSearchable
                            />
                            {displayError("acc_cost_center_id")}
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control jv-num"
                            placeholder="0.00"
                            name="debit"
                            value={x.debit || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          />
                          {displayError("debit")}
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control jv-num"
                            placeholder="0.00"
                            name="credit"
                            value={x.credit || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          />
                          {displayError("credit")}
                        </td>
                        <td>
                          <select
                            className="form-control"
                            name="budget_id"
                            value={x.budget_id || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          >
                            <option value="">Select Budget Plan</option>
                            {Array.from(budgetPlan).map((item, index) => (
                              <option value={item.id} key={item.id}>
                                {item.purpose}
                              </option>
                            ))}
                          </select>
                          {displayError("budget_id")}
                        </td>
                        <td>
                          <div className="col-lg">
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              placeholder="Select Sub Category"
                              name="sub_category"
                              options={subCategoryOptionsByRow[i] || []}
                              value={
                                (subCategoryOptionsByRow[i] || []).find(
                                  (option) => option.value === x.sub_category
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                selectHandleSubCategoryChange(selectedOption, i)
                              }
                              noOptionsMessage={() =>
                                x.chart_of_account_id
                                  ? "No sub category for this account"
                                  : "Select a chart of account first"
                              }
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                              maxMenuHeight={320}
                              isClearable
                              isSearchable
                            />
                            {displayError("sub_category")}
                          </div>
                        </td>
                        <ReferenceCells
                          line={x}
                          options={openReferences[x.chart_of_account_id]}
                          onChange={(name, value) =>
                            handleReferenceChange(i, name, value)
                          }
                        />
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            name="bill_no"
                            value={x.bill_no || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          />
                          {displayError("bill_no")}
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control"
                            name="bill_date"
                            value={x.bill_date || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          />
                          {displayError("bill_date")}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            name="mature_day"
                            value={x.mature_day || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          />
                          {displayError("mature_day")}
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            name="remarks"
                            maxLength={255}
                            placeholder="Line remarks"
                            value={x.remarks || ""}
                            onChange={(e) => handleInputChange(e, i)}
                          />
                          {displayError("remarks")}
                        </td>
                        <td>
                          {list.length > 2 ? (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm mg-t-4"
                              onClick={() => removeList(i)}
                              title="Remove line"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>
                        Total{" "}
                        <span className="jv-head-note">
                          ({list.length} lines)
                        </span>
                      </th>
                      <th></th>
                      <th className="jv-num">{money(debitSum)}</th>
                      <th className="jv-num">{money(creditSum)}</th>
                      <th colSpan="9">
                        <BalanceIndicator debit={debitSum} credit={creditSum} />
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={addMoreList}
              >
                <i className="fa fa-plus mg-r-6"></i>Add Line
              </button>
            </div>
            </div>
          </div>

          <div className="jv-form-panel">
            <div className="jv-form-head">
              <span>
                <i className="fa fa-commenting-o me-2"></i>Remarks{" "}
                <i className="fa fa-star required"></i>
              </span>
              <span className="jv-head-note">
                {String(inputs.remarks || "").length}/255
              </span>
            </div>
            <div className="jv-form-body">
              <textarea
                className={`form-control${
                  error && error.remarks ? " is-invalid" : ""
                }`}
                name="remarks"
                rows="3"
                maxLength={255}
                style={{ width: "100%" }}
                value={inputs.remarks || ""}
                onChange={handleChange}
                placeholder="Narration: what this voucher records and why"
                data-required
              />
              {displayError("remarks")}
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mg-b-20">
            <Link to="/journal-voucher" className="btn btn-outline-secondary">
              Cancel
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              onClick={formSubmit}
              disabled={saving}
            >
              <i
                className={`fa ${
                  saving ? "fa-spinner fa-spin" : "fa-send"
                } mg-r-10`}
              ></i>
              {saving ? "Saving..." : "Post Voucher"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;
